/**
 * Acceso al módulo Gestión Campaña (menú + rutas /panel-marketing/*).
 * - tipo_usuario === 'Administrador', o
 * - id numérico en GESTION_CAMPANA_EXTRA_USER_IDS (requiere que el login guarde `id` en userData), o
 * - email (minúsculas) en GESTION_CAMPANA_EXTRA_EMAILS (útil si el login no trae id).
 *
 * Si el API del panel valida rol en backend, hay que alinear también allí.
 */

export const GESTION_CAMPANA_EXTRA_USER_IDS = Object.freeze([175])

/** Correos con acceso adicional (mismo formato que en userData.email, se compara en minúsculas). */
export const GESTION_CAMPANA_EXTRA_EMAILS = Object.freeze(['salvear@interfactor.cl', 'ralegria@interfactor.cl'])

/** Correos con acceso al módulo Clientes No Operados. */
export const NO_OPERADOS_EMAILS = Object.freeze(['dvalenzuela@interfactor.cl', 'salvear@interfactor.cl'])

/**
 * Único correo autorizado para Control de gestión
 * (Clasificación RUTs, Venta anual, etc.).
 * Sin bypass de Administrador: solo este usuario.
 */
export const CONTROL_GESTION_ALLOWED_EMAIL = 'oocares@interfactor.cl'

/** @deprecated Usar CONTROL_GESTION_ALLOWED_EMAIL */
export const CLASIFICACION_RUTS_ALLOWED_EMAIL = CONTROL_GESTION_ALLOWED_EMAIL

/** @deprecated Lista legacy; seguimiento ejecutivo usa solo rol (ver canAccessSeguimientoEjecutivo). */
export const PRUEBA_SOFIA_EMAILS = Object.freeze(['salvear@interfactor.cl', 'ralegria@interfactor.cl'])

function userNumericId(user) {
  if (!user || typeof user !== 'object') return null
  const raw =
    user.id ??
    user.ID ??
    user.Id ??
    user.id_usuario ??
    user.ID_USUARIO ??
    user.IdUsuario ??
    user.idUsuario ??
    user.usuario_id ??
    user.USUARIO_ID
  if (raw == null || raw === '') return null
  const n = Number(raw)
  return Number.isFinite(n) ? n : null
}

function userEmailNorm(user) {
  if (!user || typeof user !== 'object') return ''
  return String(user.email || user.mail || user.MAIL || '')
    .trim()
    .toLowerCase()
}

function normalizeRoleKey(user) {
  if (!user || typeof user !== 'object') return ''
  const role = String(user.tipo_usuario || user.tipoUsuario || user.rol || '')
    .trim()
    .toUpperCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
  return role.replace(/[^A-Z0-9]/g, '')
}

function isAdminUser(user) {
  return normalizeRoleKey(user) === 'ADMINISTRADOR'
}

function isGerenciaUser(user) {
  const key = normalizeRoleKey(user)
  return key === 'GERENCIA' || key.startsWith('GERENCIA')
}

function isSubgerenteUser(user) {
  const key = normalizeRoleKey(user)
  return key === 'SUBGERENTE' || key.startsWith('SUBGERENTE')
}

/** TIPO_USUARIO en MGC.USUARIOS, p. ej. "Lider de equipo" → LIDERDEEQUIPO */
function isLiderEquipoUser(user) {
  const key = normalizeRoleKey(user)
  return key === 'LIDERDEEQUIPO' || key.startsWith('LIDERDE')
}

/** Correos con rol operativo de Aplicaciones (cursado de excepciones de mora).
 *  Sirve aunque no exista el TIPO_USUARIO "Aplicaciones" en el catálogo. */
export const APLICACIONES_EMAILS = Object.freeze(['aplicaciones@interfactor.cl'])

/** Cursado de excepciones de mora (cambio de tasa en factoring). */
export function isAplicacionesUser(user) {
  if (normalizeRoleKey(user) === 'APLICACIONES') return true
  const em = userEmailNorm(user)
  return Boolean(em && APLICACIONES_EMAILS.some((e) => em === String(e).trim().toLowerCase()))
}

/** TIPO_USUARIO = Comercial: solo Panel Comercial y Panel Gerencial. */
export function isComercialUser(user) {
  return normalizeRoleKey(user) === 'COMERCIAL'
}

/**
 * Seguimiento ejecutivo (ruta /panel-marketing/seguimiento-ejecutivo):
 * - Administrador / Gerencia: todo
 * - Subgerente / Lider de equipo: solo su equipo
 */
export function canAccessSeguimientoEjecutivo(user) {
  if (!user) return false
  if (isAplicacionesUser(user)) return false
  return (
    isAdminUser(user) ||
    isGerenciaUser(user) ||
    isSubgerenteUser(user) ||
    isLiderEquipoUser(user)
  )
}

/** Misma llave que la ruta (RequirePruebaSofiaAccess). */
export function canSeeSeguimientoEjecutivoMenu(user) {
  return canAccessSeguimientoEjecutivo(user)
}

export function canAccessGestionCampana(user) {
  if (!user) return false
  if (isAplicacionesUser(user)) return false
  if (isAdminUser(user)) return true
  const uid = userNumericId(user)
  if (uid != null && GESTION_CAMPANA_EXTRA_USER_IDS.includes(uid)) return true
  const em = userEmailNorm(user)
  if (em && GESTION_CAMPANA_EXTRA_EMAILS.some((e) => em === String(e).trim().toLowerCase())) return true
  return false
}

export function canAccessNoOperados(user) {
  if (!user) return false
  if (isAplicacionesUser(user)) return false
  if (isAdminUser(user)) return true
  const em = userEmailNorm(user)
  return em && NO_OPERADOS_EMAILS.some((e) => em === String(e).trim().toLowerCase())
}

/** Control de gestión: solo el correo en CONTROL_GESTION_ALLOWED_EMAIL. */
export function canAccessControlGestion(user) {
  if (!user) return false
  if (isAplicacionesUser(user)) return false
  const allowed = String(CONTROL_GESTION_ALLOWED_EMAIL || '')
    .trim()
    .toLowerCase()
  if (!allowed) return false
  return userEmailNorm(user) === allowed
}

/** Clasificación RUTs. */
export function canAccessClasificacionRuts(user) {
  return canAccessControlGestion(user)
}

/** Venta anual por listado de RUTs. */
export function canAccessVentaAnual(user) {
  return canAccessControlGestion(user)
}

/** Menú Control de gestión. */
export function canAccessControlGestionMenu(user) {
  return canAccessControlGestion(user)
}

/** Alias del acceso a /panel-marketing/seguimiento-ejecutivo */
export function canAccessPruebaSofia(user) {
  return canAccessSeguimientoEjecutivo(user)
}

/** Menú Gestión Campaña. */
export function canAccessMarketingMenu(user) {
  return (
    canAccessGestionCampana(user) ||
    canAccessNoOperados(user) ||
    canAccessSeguimientoEjecutivo(user)
  )
}

/** Recaptura / Activación Comercial — matriz, ficha, bitácora. */
export function canAccessRecapturaOperativo(user) {
  if (!user) return false
  if (isAplicacionesUser(user) || isComercialUser(user)) return false
  const key = normalizeRoleKey(user)
  return (
    isAdminUser(user) ||
    isGerenciaUser(user) ||
    isSubgerenteUser(user) ||
    isLiderEquipoUser(user) ||
    key === 'EJECUTIVO' ||
    key.startsWith('EJECUTIVO')
  )
}

/** Resumen gerencial Recaptura. */
export function canAccessRecapturaResumen(user) {
  if (!user) return false
  if (isAplicacionesUser(user) || isComercialUser(user)) return false
  return (
    isAdminUser(user) ||
    isGerenciaUser(user) ||
    isSubgerenteUser(user) ||
    isLiderEquipoUser(user)
  )
}

/** Aprobaciones Recaptura (Gerencia / Admin). */
export function canDecideRecaptura(user) {
  if (!user) return false
  if (isAplicacionesUser(user) || isComercialUser(user)) return false
  return isAdminUser(user) || isGerenciaUser(user)
}

/** Carga Excel Recaptura (Admin / Gerencia). */
export function canAccessRecapturaCarga(user) {
  if (!user) return false
  if (isAplicacionesUser(user) || isComercialUser(user)) return false
  return isAdminUser(user) || isGerenciaUser(user)
}

/** Menú lateral Recaptura (cualquier subvista permitida). */
export function canAccessRecapturaMenu(user) {
  return (
    canAccessRecapturaOperativo(user) ||
    canAccessRecapturaResumen(user) ||
    canDecideRecaptura(user) ||
    canAccessRecapturaCarga(user)
  )
}

/** Parametrización del cotizador factoring (vista Gerente). */
export function canAccessCotizadorGerente(user) {
  if (!user) return false
  if (isAplicacionesUser(user)) return false
  return isAdminUser(user) || isGerenciaUser(user) || canAccessGestionCampana(user)
}

/** Menú y rutas del cotizador factoring — Administrador o Gerencia. */
export function canAccessCotizadorFactoring(user) {
  if (!user) return false
  if (isAplicacionesUser(user)) return false
  return isAdminUser(user) || isGerenciaUser(user)
}

/** Consulta otorgamientos (Caja de herramientas) — Administrador o Gerencia. */
export function canAccessConsultaOtorgamientos(user) {
  if (!user) return false
  if (isAplicacionesUser(user)) return false
  return isAdminUser(user) || isGerenciaUser(user)
}

/** Panel Comercial / Gerencial — Administrador, Gerencia o Comercial. */
export function canAccessPanelComercial(user) {
  if (!user) return false
  if (isAplicacionesUser(user)) return false
  return isAdminUser(user) || isGerenciaUser(user) || isComercialUser(user)
}

/** Asignar Gestionador y comentario en el Panel Comercial. */
export function canAssignPanelGestionador(user) {
  if (!user) return false
  if (isAplicacionesUser(user)) return false
  return isAdminUser(user) || isGerenciaUser(user) || isComercialUser(user)
}

/** Correos con acceso a Análisis de precios (además de Administrador). */
export const ANALISIS_PRECIOS_ALLOWED_EMAILS = Object.freeze(['ralegria@interfactor.cl'])

/** Análisis de precios (Caja de herramientas) — Administrador o whitelist. */
export function canAccessAnalisisPrecios(user) {
  if (!user) return false
  if (isAdminUser(user)) return true
  const em = userEmailNorm(user)
  return Boolean(em && ANALISIS_PRECIOS_ALLOWED_EMAILS.some((e) => em === String(e).trim().toLowerCase()))
}

/** Correos con acceso a Seguimiento cesiones/facturación (además de Administrador). */
export const SEGUIMIENTO_CESIONES_ALLOWED_EMAILS = Object.freeze(['ralegria@interfactor.cl'])

/** Seguimiento cesiones/facturación (Caja de herramientas) — Administrador o whitelist. */
export function canAccessSeguimientoCesionesFacturacion(user) {
  if (!user) return false
  if (isAdminUser(user)) return true
  const em = userEmailNorm(user)
  return Boolean(
    em && SEGUIMIENTO_CESIONES_ALLOWED_EMAILS.some((e) => em === String(e).trim().toLowerCase()),
  )
}

/**
 * Piloto productivo: simulador solo para estos ejecutivos (+ Administrador).
 * Vaciar / ampliar la lista cuando el módulo se abra a toda la fuerza comercial.
 * Bandeja (Líder / Gerencia / Aplicaciones) no usa esta lista.
 */
export const MORA_SIMULADOR_PILOTO_EMAILS = Object.freeze(['jmaldonado@interfactor.cl'])

/** Bandeja / reporte de excepciones de mora — Gerencia, Líder, Admin o Aplicaciones. */
export function canAccessSolicitudesExcepcionMora(user) {
  if (!user) return false
  return (
    isAdminUser(user) ||
    isGerenciaUser(user) ||
    isLiderEquipoUser(user) ||
    isSubgerenteUser(user) ||
    isAplicacionesUser(user)
  )
}

/** Simulador de mora: piloto (whitelist) o Administrador. */
export function canAccessSimuladorMora(user) {
  if (!user) return false
  if (isAplicacionesUser(user) || isComercialUser(user)) return false
  if (isAdminUser(user)) return true
  const em = userEmailNorm(user)
  return Boolean(
    em && MORA_SIMULADOR_PILOTO_EMAILS.some((e) => em === String(e).trim().toLowerCase()),
  )
}

/** Mantenedor de tasas mora preacordadas — solo Aplicaciones o Administrador. */
export function canAccessTasasMoraPreacordadas(user) {
  if (!user) return false
  return isAdminUser(user) || isAplicacionesUser(user)
}

/** Carga masiva de leads desde Excel — solo Administrador (asigna leads a terceros). */
export function canAccessCargaMasivaLeads(user) {
  if (!user) return false
  if (isAplicacionesUser(user) || isComercialUser(user)) return false
  return isAdminUser(user)
}

/** Config plazo N días (pagos confirmados) — Gerencia o Administrador. */
export function canAccessConfigPlazoMoraPagos(user) {
  if (!user) return false
  return isAdminUser(user) || isGerenciaUser(user)
}

/** Home path tras login / root redirect. */
export function defaultHomePath(user) {
  if (isAplicacionesUser(user)) return '/solicitudes-excepcion-mora'
  if (isComercialUser(user)) return '/panel-comercial'
  return '/inicio'
}

const COMERCIAL_ALLOWED_PREFIXES = ['/panel-comercial', '/panel-gerencial', '/cambioclave']

export function isComercialAllowedPath(pathname) {
  const path = String(pathname || '')
  return COMERCIAL_ALLOWED_PREFIXES.some((p) => path === p || path.startsWith(`${p}/`))
}
